import { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Warning, ArrowSquareOut } from "@phosphor-icons/react";
import { VISIT_STATUS, daysBetween, fmtDay, todayIso } from "./contractUtils";
import { StarRating, RATING_WORDS } from "./StarRating";

import DateInput from "../DateInput";
const ShortNotice = ({ date, booking }) => {
  if (!date) return null;
  const d = daysBetween(todayIso(), date);
  if (d < booking.min_notice_days) return <p className="text-xs text-red-600 flex items-center gap-1" data-testid="booking-too-soon"><Warning size={13} weight="fill" /> Needs at least {booking.min_notice_days} day{booking.min_notice_days !== 1 ? "s" : ""} notice.</p>;
  if (d < booking.short_notice_days) return <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="booking-short-notice"><Warning size={13} weight="fill" /> Short notice — we'll confirm instantly and alert our team straight away.</p>;
  return null;
};

/** Client portal: book a new visit on `date`. */
export function ClientBookDialog({ contract, date, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const b = contract.booking;
  const submit = async () => {
    setSaving(true);
    try { const { data } = await API.post(`/client-hub/contracts/${contract.id}/bookings`, { date, note }); toast.success(data.message); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not book that date"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="client-book-dialog">
        <DialogHeader><DialogTitle>Book a visit</DialogTitle><DialogDescription>{contract.title} · {fmtDay(date)}{contract.start_time ? ` · ${contract.start_time}` : ""}</DialogDescription></DialogHeader>
        <ShortNotice date={date} booking={b} />
        <div><Label className="text-xs">Anything we should know? <span className="text-muted-foreground">(optional)</span></Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please focus on the meeting rooms" data-testid="client-book-note" /></div>
        <p className="text-[11px] text-muted-foreground">Your booking is confirmed straight away{contract.team ? ` and ${contract.team} will be notified` : ""}.</p>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose} data-testid="client-book-cancel">Cancel</Button><Button onClick={submit} disabled={saving || daysBetween(todayIso(), date) < b.min_notice_days} data-testid="client-book-confirm">{saving ? "Booking…" : "Confirm booking"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

/** Star + comment form for a completed visit; re-submitting updates the client's rating. */
function VisitRatingForm({ contract, visit, onDone }) {
  const [stars, setStars] = useState(visit.rating?.stars || 0);
  const [comment, setComment] = useState(visit.rating?.comment || "");
  const [editing, setEditing] = useState(!visit.rating);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!stars) return toast.error("Pick a star rating first");
    setBusy(true);
    try { const { data } = await API.post(`/client-hub/contracts/${contract.id}/bookings/${visit.id}/rating`, { stars, comment }); toast.success(data.message); setEditing(false); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save your rating"); }
    finally { setBusy(false); }
  };
  if (!editing && visit.rating) {
    return (
      <div className="border-t border-border pt-3" data-testid="client-visit-rating-saved">
        <div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2"><StarRating value={visit.rating.stars} size={16} testid="client-visit-rating-stars" /><span className="text-xs font-medium">{RATING_WORDS[visit.rating.stars]}</span></div><button type="button" className="text-[11px] text-primary hover:underline" onClick={() => setEditing(true)} data-testid="client-visit-rating-edit">Change</button></div>
        {visit.rating.comment && <p className="text-xs text-muted-foreground mt-1">“{visit.rating.comment}”</p>}
        <p className="text-[10px] text-muted-foreground mt-1">Rated by {visit.rating.by_name}{visit.rating.at ? ` · ${new Date(visit.rating.at).toLocaleDateString("en-GB")}` : ""}</p>
      </div>
    );
  }
  return (
    <div className="border-t border-border pt-3 space-y-2" data-testid="client-visit-rating-form">
      <div className="flex items-center justify-between gap-2"><Label className="text-xs">How was this visit?</Label><span className="text-xs text-muted-foreground">{stars ? RATING_WORDS[stars] : "Tap a star"}</span></div>
      <StarRating value={stars} onChange={setStars} size={26} testid="client-rating-star" />
      <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything we should know? (optional)" data-testid="client-rating-comment" />
      <div className="flex justify-end gap-2">{visit.rating && <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="client-rating-cancel">Cancel</Button>}<Button size="sm" onClick={submit} disabled={busy || !stars} data-testid="client-rating-submit">{busy ? "Saving…" : visit.rating ? "Update rating" : "Submit rating"}</Button></div>
    </div>
  );
}

/** Client portal: view a visit; move or cancel it when allowed; rate it once completed. */
export function ClientVisitDialog({ contract, visit, onClose, onDone }) {
  const [moveTo, setMoveTo] = useState("");
  const [busy, setBusy] = useState(false);
  if (!visit) return null;
  const st = VISIT_STATUS[visit.status] || VISIT_STATUS.scheduled;
  const b = contract.booking;
  const canChange = visit.can_change && contract.status === "active" && b.enabled;
  const move = async () => {
    setBusy(true);
    try { const { data } = await API.put(`/client-hub/contracts/${contract.id}/bookings/${visit.id}`, { date: moveTo }); toast.success(data.message); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not move the visit"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(`Cancel the visit on ${fmtDay(visit.date)}?`)) return;
    setBusy(true);
    try { const { data } = await API.delete(`/client-hub/contracts/${contract.id}/bookings/${visit.id}`); toast.success(data.message); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel the visit"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!visit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="client-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">{fmtDay(visit.date)} <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${st.cls}`}>{st.label}</span></DialogTitle>
          <DialogDescription>{contract.title}{visit.start_time ? ` · ${visit.start_time}${visit.end_time ? `–${visit.end_time}` : ""}` : ""}{visit.team ? ` · ${visit.team}` : ""}{visit.booked_by === "client" ? " · booked by you" : ""}</DialogDescription>
        </DialogHeader>
        {visit.note && <p className="text-xs bg-muted/40 rounded-md p-2">Your note: {visit.note}</p>}
        {visit.status === "completed" && <p className="text-xs text-emerald-700">Completed{visit.completed_at ? ` on ${new Date(visit.completed_at).toLocaleString("en-GB")}` : ""}.</p>}
        {visit.can_rate && <VisitRatingForm contract={contract} visit={visit} onDone={onDone} />}
        <Link to={`/portal/jobs/${visit.id}`} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="client-visit-open-job"><ArrowSquareOut size={12} /> View visit details, photos & notes</Link>
        {canChange ? (
          <div className="border-t border-border pt-3 space-y-3">
            <div>
              <Label className="text-xs">Move to another date</Label>
              <div className="flex gap-2 mt-1"><DateInput min={b.earliest} max={b.latest || undefined} value={moveTo} onChange={(e) => setMoveTo(e.target.value)} data-testid="client-visit-move-date" /><Button size="sm" onClick={move} disabled={!moveTo || busy || daysBetween(todayIso(), moveTo) < b.min_notice_days} data-testid="client-visit-move-btn">Move</Button></div>
              <ShortNotice date={moveTo} booking={b} />
            </div>
            <div className="flex justify-between items-center"><p className="text-[11px] text-muted-foreground">Need to skip this one? Cancellations need {b.min_notice_days} day{b.min_notice_days !== 1 ? "s" : ""} notice.</p><Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={cancel} disabled={busy} data-testid="client-visit-cancel-btn">Cancel visit</Button></div>
          </div>
        ) : visit.status === "scheduled" && <p className="text-[11px] text-muted-foreground">This visit can no longer be changed online — please contact us if you need to.</p>}
      </DialogContent>
    </Dialog>
  );
}
